import {DOM} from '../helpers/DOM.js';
import {Helper} from '../helpers/Helper.js';

export class MyAccountGamificationMyCampaign {
	#page;
	#chartData = {};
	#additionalData = {};
	#activeTimeline = '1y';
	#chartFetched = false;
	#fetching = {};

	constructor() {
		this.#page = DOM.el('.gamification-my-campaign-page');
		if (!this.#page) {
			return;
		}

		this.#bindEvents();
		this.#fetchChart();
		this.#fetchAdditionalData();
	}

	#bindEvents() {
		DOM.addEvent('click', '.gamification-my-campaign-page .period_buttons button', (event, button) => {
			event.preventDefault();
			if (this.#fetching.chart) {
				return;
			}
			this.#activeTimeline = button.dataset.period;
			DOM.el('.gamification-my-campaign-page .period_buttons button.active')?.classList.remove('active');
			button.classList.add('active');
			this.#fetchChart();
		});

		DOM.addEvent('click', '.gamification-my-campaign-page .html_chart_wrapper .arrow-left', () => {
			DOM.el('.gamification-my-campaign-page .html_chart')?.scrollBy({left: -150, behavior: 'smooth'});
		});

		DOM.addEvent('click', '.gamification-my-campaign-page .html_chart_wrapper .arrow-right', () => {
			DOM.el('.gamification-my-campaign-page .html_chart')?.scrollBy({left: 150, behavior: 'smooth'});
		});
	}

	async #fetchChart() {
		const chart = DOM.el('.gamification-my-campaign-chart', this.#page);
		if (!chart?.dataset.action) {
			return;
		}

		if (this.#fetching.chart) {
			return;
		}

		this.#fetching.chart = true;
		const {Helper} = await import('../helpers/Helper.js' /* webpackChunkName: "helper" */);

		try {
			const response = await Helper.request(admin_ajax[0], {
				action: chart.dataset.action,
				token: chart.dataset.token,
				timeline: this.#activeTimeline,
			});

			if (response.success) {
				this.#chartData = {
					totalToday: response.total_today,
					goal: response.goal,
					totalRaised: response.total_raised,
					data: response.chart_data || [],
				};
				await this.#renderChart(chart);
				this.#chartFetched = true;
			}
		} catch (error) {
			console.error(error);
		} finally {
			this.#fetching.chart = false;
		}
	}

	async #fetchAdditionalData() {
		const wrapper = DOM.el('.gamification-my-campaign-additional-data', this.#page);
		if (!wrapper?.dataset.action) {
			return;
		}

		const {Helper} = await import('../helpers/Helper.js' /* webpackChunkName: "helper" */);

		try {
			const response = await Helper.request(admin_ajax[0], {
				action: wrapper.dataset.action,
				token: wrapper.dataset.token,
			});

			if (response.success) {
				this.#additionalData = response;
				this.#renderAdditionalData();
			}
		} catch (error) {
			console.error(error);
		}
	}

	async #renderChart(chart) {
		const currencySymbol = chart.dataset.currencySymbol || '';
		const progressbar = DOM.el('.progress-bar-wrapper', chart);
		const totalsToday = DOM.el('.totals_total', chart);
		const raised = DOM.els('.total_raised', chart);
		const goal = DOM.el('.goal', chart);
		const htmlChart = DOM.el('.html_chart', chart);

		let chartMax = 0;
		(this.#chartData.data || []).forEach((bar) => {
			if (bar.value > chartMax) {
				chartMax = bar.value;
			}
		});

		htmlChart.innerHTML = '';

		if (this.#chartData.data?.length) {
			const bars = [];
			htmlChart.append(
				...this.#chartData.data.map((bar) => {
					const value = bar.value ? bar.value / (chartMax / 100) : 0;
					const barElement = DOM.create('div', {
						className: ['chart_bar', !bar.value ? 'empty' : ''],
						dataValue: currencySymbol + Number(bar.value || 0).toFixed(2),
					}, [
						DOM.create('div', {className: 'chart_bar_inner'}, [DOM.create('span')]),
						DOM.create('div', {
							className: 'chart_bar_label',
							dataLevTooltip: bar.label_description,
						}, bar.label),
					]);

					if (bar.value) {
						bars.push({barElement, value});
					}
					return barElement;
				}),
				DOM.create('span', {className: 'placeholder'})
			);

			bars.forEach((item, index) => {
				setTimeout(() => {
					const inner = DOM.el('.chart_bar_inner span', item.barElement);
					if (inner) {
						inner.style.height = `${item.value}%`;
					}
				}, 50 * (index + 1));
			});
		} else {
			htmlChart.append(
				DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'levcharity'))
			);
		}

		if (!this.#chartFetched) {
			const goalAmount = Number(this.#chartData.goal) || 0;
			const raisedAmount = Number(this.#chartData.totalRaised) || 0;
			const percentage = goalAmount > 0 ? Math.min(100, (raisedAmount / goalAmount) * 100) : 0;

			if (progressbar) {
				progressbar.style.setProperty('--progress', percentage);
			}

			const animations = [];
			if (totalsToday) {
				animations.push(this.#numberAnimation(this.#chartData.totalToday, totalsToday, currencySymbol));
			}
			raised.forEach((el) => {
				animations.push(this.#numberAnimation(this.#chartData.totalRaised, el, currencySymbol));
			});
			if (goal) {
				animations.push(this.#numberAnimation(this.#chartData.goal, goal, currencySymbol));
			}
			if (progressbar) {
				animations.push(
					this.#numberAnimation(
						percentage,
						DOM.el('.circular-progress-percentage', progressbar),
						'',
						'%',
						0,
						false
					)
				);
			}
			await Promise.all(animations);
		}
	}

	#renderAdditionalData() {
		const currencySymbol = Helper.resolveCurrencySymbol(
			this.#additionalData.currency_symbol,
			this.#page?.dataset.currencySymbol
		);
		const raisedEl = DOM.el('.total_raised_value', this.#page);
		const donorsEl = DOM.el('.total_donors_value', this.#page);

		if (raisedEl) {
			this.#numberAnimation(this.#additionalData.total_raised, raisedEl, currencySymbol);
		}
		if (donorsEl) {
			this.#numberAnimation(this.#additionalData.total_donors, donorsEl, '', '', 0, false);
		}

		this.#renderLeaderboardList(
			DOM.el('.top-donors-list', this.#page),
			this.#additionalData.my_donations || []
		);
		this.#renderLeaderboardList(
			DOM.el('.top-participants-list', this.#page),
			this.#additionalData.top_participants || []
		);
		this.#renderCaptainTeams(DOM.el('.top-captain-teams-list', this.#page), this.#additionalData.top_captain_teams || []);
	}

	#renderLeaderboardList(container, items) {
		if (!container) {
			return;
		}

		container.innerHTML = '';

		if (!items.length) {
			container.append(
				DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'levcharity'))
			);
			return;
		}

		container.append(
			...items.map((item) => {
				const row = DOM.create('div', {className: 'leaderboard-row'});
				const rank = DOM.create('span', {className: 'leaderboard-rank'}, String(item.rank));
				const avatar = DOM.create('div', {className: 'leaderboard-avatar'});

				if (item.image) {
					avatar.append(DOM.create('img', {src: item.image, alt: item.name}));
				} else {
					avatar.innerHTML = this.#defaultAvatarSvg();
				}

				const info = DOM.create('div', {className: 'leaderboard-info'}, [
					DOM.create('span', {className: 'leaderboard-name'}, item.name),
					DOM.create('span', {className: 'leaderboard-subtitle'}, item.subtitle),
				]);

				const amount = DOM.create('span', {className: 'leaderboard-amount'}, item.amount_formatted);

				row.append(rank, avatar, info, amount);

				if (item.url) {
					const link = DOM.create('a', {href: item.url, className: 'leaderboard-row-link'});
					link.append(row);
					return link;
				}

				return row;
			})
		);
	}

	#renderCaptainTeams(container, items) {
		if (!container) {
			return;
		}

		container.innerHTML = '';

		if (!items.length) {
			container.append(
				DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'levcharity'))
			);
			return;
		}

		container.append(
			...items.map((item) => {
				const card = DOM.create('div', {className: 'captain-team-card'});
				const label = DOM.create('span', {className: 'captain-team-label'});
				label.textContent = `${wp.i18n.__('Captain of:', 'levcharity-gamification')} `;
				label.append(DOM.create('a', {href: item.url}, item.title));

				const header = DOM.create('div', {className: 'captain-team-header'}, [
					DOM.create('span', {className: 'captain-team-icon', ariaHidden: 'true'}),
					label,
				]);

				const progressTrack = DOM.create('div', {className: 'captain-team-progress-track'}, [
					DOM.create('div', {
						className: 'captain-team-progress-fill',
						style: `width: ${Math.min(100, item.progress || 0)}%`,
					}),
				]);

				const footer = DOM.create('div', {className: 'captain-team-footer'}, [
					DOM.create('span', {className: 'captain-team-raised'}, ''),
					DOM.create('span', {className: 'captain-team-goal'}, ''),
				]);

				card.append(header, progressTrack, footer);
				container.append(card);

				const raisedEl = DOM.el('.captain-team-raised', footer);
				const goalEl = DOM.el('.captain-team-goal', footer);
				this.#numberAnimation(
					item.raised,
					raisedEl,
					Helper.resolveCurrencySymbol(item.currency_symbol, this.#page?.dataset.currencySymbol),
					` ${wp.i18n.__('raised', 'levcharity-gamification')}`
				);
				this.#numberAnimation(
					item.goal,
					goalEl,
					Helper.resolveCurrencySymbol(item.currency_symbol, this.#page?.dataset.currencySymbol)
				);

				return card;
			})
		);
	}

	#defaultAvatarSvg() {
		return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M41.3376 41.3376C39.5983 43.0887 37.7063 44.511 35.7204 45.5885C28.862 50.1964 19.138 50.1964 12.2796 45.5885C10.2937 44.511 8.40168 43.0887 6.66239 41.3376C-2.35413 32.3311 -2.35413 15.6689 6.66239 6.66239C15.6689 -2.35413 32.3311 -2.35413 41.3376 6.66239C50.3541 15.6689 50.3541 32.3311 41.3376 41.3376Z" fill="#00ADEF"/><path d="M24.0007 27.1299C28.6422 27.1299 32.4137 23.3584 32.4137 18.7169C32.4137 14.0754 28.6422 10.3039 24.0007 10.3039C19.3592 10.3039 15.5877 14.0754 15.5877 18.7169C15.5877 23.3584 19.3592 27.1299 24.0007 27.1299Z" fill="#0076A3"/><path d="M36.5218 39.3303V45.5885C28.6634 50.1964 18.9394 50.1964 11.081 45.5885V39.3303C11.081 32.2933 16.6838 26.6905 24.0009 26.6905C31.3181 26.6905 36.5218 32.2933 36.5218 39.3303Z" fill="#0076A3"/></svg>`;
	}

	#numberAnimation(to, el, prefix = '', suffix = '', from = 0, withFractions = true) {
		return new Promise((resolve) => {
			if (!el) {
				resolve();
				return;
			}

			import('gsap').then(({gsap}) => {
				const tweenObj = {value: Number(from) || 0};
				const targetValue = Number(to) || 0;

				gsap.to(tweenObj, {
					value: targetValue,
					duration: 0.25,
					ease: 'power2.inOut',
					onUpdate: () => {
						const v = Number(tweenObj.value) || 0;
						el.textContent = `${prefix}${v.toFixed(withFractions ? 2 : 0)}${suffix}`;
					},
					onComplete: resolve,
				});
			}).catch(() => resolve());
		});
	}
}
